import PropTypes from 'prop-types';
import NumberFormat from 'react-number-format';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
// import { MdVisibility } from 'react-icons/md';
import { useState } from 'react';
import { FaCheck, FaRegQuestionCircle, FaTimes } from 'react-icons/fa';
import _ from 'lodash';
import { useRouter } from 'next/router';
import currencySymbol from '../../../helpers/currencySymbol';
import TripBookButton from '../../TripConfiguration/ViewTripConfigurationItems/TripBookButton';
import { addToTrip } from '../../../store/slices/trip';
import checkDoShowMarkupRates from '../../../helpers/selectors/checkDoShowMarkupRates';
import { PageLinks, SearchModes, TravelTypes } from '../../../constants';
import getPaymentTypeLabel from '../../../helpers/getPaymentTypeLabel';
import Modal from '../../Modal';
import { getRoomDescriptionRequest } from '../../../store/slices/hotel';
import {
  getFullRawDescription,
  getRateNameFromCode,
} from '../../TravelOption/HotelInformation/HotelDetails/RoomsDetails';
import DetailedRoomDescription from '../../TravelOption/HotelInformation/HotelDetails/DetailedRoomDescription';
import HotelCancellationLabel from '../../TravelOption/HotelInformation/HotelCancellationLabel';
import Label from '../../Label';
import RateCodeKeys from '../../TravelOption/HotelInformation/HotelFilterBar/RateCodeKey';

const HotelRate = ({ product, tripId, hotelDetails }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const doShowMarkupRates = useSelector(
    checkDoShowMarkupRates({
      mode: TravelTypes.hotel,
      tripId,
    }),
  );
  const hotelRateCodes = useSelector((store) => store.user.hotelRateCodes);

  const { doShowRateCodesFoldedIntoGroupInLabel, doShowRateCodeGroupName } = useSelector(
    (store) => {
      const { travelAgency } = store.user;
      const settings = (travelAgency || {}).settings || {};

      return {
        doShowRateCodesFoldedIntoGroupInLabel:
          settings.doShowRateCodesFoldedIntoGroupInLabel !== undefined &&
          settings.doShowRateCodesFoldedIntoGroupInLabel !== null
            ? settings.doShowRateCodesFoldedIntoGroupInLabel
            : true,
        doShowRateCodeGroupName: settings.doShowRateCodeGroupName || false,
      };
    },
  );

  const { travelAgency: currentTravelAgency } = useSelector((state) => state.user);
  const { disableMultipleRoomBookings } =
    (currentTravelAgency && currentTravelAgency.settings && currentTravelAgency.settings) || {};
  const isDedicatedHotelPage = router.route === PageLinks.DedicatedHotelPage.route;

  const { roomDescriptions } = useSelector((state) => state.hotel);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { code } = product;
  const room = product.rooms && product.rooms[0] && product.rooms[0];
  const handleViewRatesClick = () => {
    setIsModalOpen(!isModalOpen);
    if (code && (!roomDescriptions[code] || !roomDescriptions[code].description)) {
      dispatch(
        getRoomDescriptionRequest({
          code,
          description: getFullRawDescription(product),
          currencyCode: product.roomPrice.propertyCurrencyCode || product.roomPrice.currencyCode,
        }),
      );
    }
  };

  const saveTripStatus = useSelector(
    (store) => (store.trip.selectedTrips[tripId] || {}).saveTripStatus,
  );
  const selectedTrip = useSelector((store) => store.trip.selectedTrips[tripId]);

  const { saveSendTripEmailStatus, saveSendTripEmailError } = useSelector((store) => store.trip);

  const handleSelectRoomClick = () => {
    if (tripId) {
      // TODO: Move this logic to a helper function. It duplicates with the same logic
      // in components\TravelOption\HotelInformation\HotelDetails\RoomsDetails\index.jsx
      // handleSelectRoomClick
      const doShowMarkupAmount =
        doShowMarkupRates && product.roomPrice.markupPrice !== product.roomPrice.originalPrice;

      const newItinerary = {
        ...hotelDetails,
        selectedRoomCode: product.code,
        selectedProduct: product,
        hotel: {
          ...hotelDetails.hotel,
          price: {
            ...hotelDetails.hotel.price,
            doShowMarkupAmount,
          },
        },
        cost: {
          ...hotelDetails.cost,
          doShowMarkupAmount,
        },
        id: +_.uniqueId(),
      };

      const updatedTrip = {
        selectedRoomCodes: disableMultipleRoomBookings
          ? [product.code]
          : [
              ...(selectedTrip && selectedTrip.selectedRoomCodes
                ? selectedTrip.selectedRoomCodes
                : []),
              product.code,
            ],
        hotelItineraries:
          disableMultipleRoomBookings || isDedicatedHotelPage
            ? [newItinerary]
            : [
                ...(selectedTrip &&
                selectedTrip.hotelItineraries &&
                Object.keys(selectedTrip.hotelItineraries).length > 0
                  ? Object.values(selectedTrip.hotelItineraries)
                  : []),
                newItinerary,
              ],
      };

      dispatch(
        addToTrip({
          tripId,
          trip: updatedTrip,
        }),
      );

      return updatedTrip;
    }
    track('roomsDetails.handleSelectRoomClick', {
      product: JSON.stringify(product),
    });
    return undefined;
  };

  if (!product) return <></>;

  return (
    <>
      <div className="w-full">
        <div className="bg-gray-300 px-4 py-2 m-0">
          <h4 className="uppercase whitespace-nowrap overflow-hidden truncate ">
            {room && room.desc}
          </h4>
          <h6>
            {room.type && `${room.type}`}
            {room.type && room.bedTypes && room.bedTypes.length > 0 ? ' - ' : ''}
            {`${room.bedTypes.join(', ')}`}
          </h6>
        </div>

        <div className="bg-gray-100">
          <div className="box-border py-2 px-4 text-sm">
            <div>
              <div className="flex items-center justify-between">
                {product && product.roomPrice && product.roomPrice.averageNightlyRateBeforeTax && (
                  <h2 className="flex items-center justify-start text-xl">
                    <span>
                      <NumberFormat
                        value={product.roomPrice.averageNightlyRateBeforeTax}
                        displayType="text"
                        thousandSeparator
                        prefix={currencySymbol(product.roomPrice.currencyCode)}
                        fixedDecimalScale
                        decimalScale={2}
                      />
                    </span>
                  </h2>
                )}
                <div className="relative w-20">
                  <TripBookButton
                    tripId={tripId}
                    saveTripStatus={saveTripStatus}
                    saveSendTripEmailStatus={saveSendTripEmailStatus}
                    saveSendTripEmailError={saveSendTripEmailError}
                    buttonColorCssClass="bg-button-theme text-button-theme"
                    buttonGroupStyles={{
                      minWidth: 'unset',
                      paddingTop: '0.25rem',
                      paddingBottom: '0.25rem',
                    }}
                    onBookClick={() => handleSelectRoomClick(undefined, product)}
                    className="float-right m-0 bg-black text-white uppercase px-5 py-1 rounded"
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center text-[85%] text-gray-500 opacity-60">
              Avg Per Night, Taxes Excluded
            </div>
            <div className="font-thin">
              <span>
                <NumberFormat
                  value={product.roomPrice.markupPrice}
                  displayType="text"
                  thousandSeparator
                  prefix={currencySymbol(product.roomPrice.currencyCode)}
                  fixedDecimalScale
                  decimalScale={2}
                />
              </span>{' '}
              for{' '}
              <span>
                {hotelDetails.duration} {hotelDetails.duration > 1 ? 'nights' : 'night'}
              </span>{' '}
              including taxes + fees
            </div>
            <div className="w-auto my-1 font-normal text-[85%] text-green-700 text-left box-border border-normal">
              <div>✓ {product.cancellationPolicy}</div>
            </div>
            <div>
              <div className="text-md text-gray-600">{room && room.category}</div>
            </div>
            <div className="grid grid-cols-5">
              <div className="col-span-4">
                <>
                  <HotelCancellationLabel product={product} />
                  &nbsp;&nbsp;
                </>
                {product.valueAdds &&
                  product.valueAdds.length > 0 &&
                  product.valueAdds.map((v) => (
                    <div key={v}>
                      <Label text={v} icon={<FaCheck />} level="success" italic inline />
                      &nbsp;&nbsp;
                    </div>
                  ))}
                {product.isMatchWithRequestedRatePlanCode ? (
                  <>
                    <Label
                      text={getRateNameFromCode(
                        room.ratePlanClientId || room.ratePlanCode,
                        hotelRateCodes,
                        doShowRateCodesFoldedIntoGroupInLabel,
                        false,
                        doShowRateCodeGroupName,
                      )}
                      icon={!product.isMatchWithRequestedRatePlanCode ? <FaTimes /> : <FaCheck />}
                      level={!product.isMatchWithRequestedRatePlanCode ? 'danger' : 'success'}
                      italic
                      inline
                      descriptionOnHover={`${getRateNameFromCode(room.ratePlanClientId || room.ratePlanCode, hotelRateCodes, doShowRateCodesFoldedIntoGroupInLabel, true, doShowRateCodeGroupName) ? `${getRateNameFromCode(room.ratePlanClientId || room.ratePlanCode, hotelRateCodes, doShowRateCodesFoldedIntoGroupInLabel, true, doShowRateCodeGroupName)} rate` : 'Rate'} code match with your selection!`}
                    />
                    &nbsp;&nbsp;
                    <RateCodeKeys
                      icon={
                        <FaRegQuestionCircle
                          className="text-green-600 min-w-4 place-self-center mr-2"
                          size={20}
                        />
                      }
                      variant="white"
                      filterSpacingClass="py-1 px-3"
                      label=""
                      selectedSearchMode={SearchModes.HOTEL}
                    />
                  </>
                ) : (
                  <></>
                )}
                {product.isPrepaidRate ? (
                  <>
                    <Label text="Prepaid rate" level="success" icon={<FaCheck />} italic inline />
                    &nbsp;&nbsp;
                  </>
                ) : (
                  ''
                )}
                {!product.isPrepaidRate &&
                product.paymentTypeCode !== undefined &&
                product.paymentTypeCode !== null ? (
                  <>
                    <Label
                      text={getPaymentTypeLabel(product.paymentTypeCode, currentTravelAgency)}
                      icon={<FaCheck />}
                      level="warning"
                      italic
                      inline
                    />
                    &nbsp;&nbsp;
                  </>
                ) : (
                  <></>
                )}
              </div>
              <div className="col-span-1">
                <button
                  type="button"
                  onClick={handleViewRatesClick}
                  className="underline inline-block whitespace-nowrap text-sm"
                >
                  Rate Detail
                </button>
              </div>

              {/* <MdVisibility onClick={handleViewRatesClick}
              className="font-normal cursor-pointer hover:text-blue-900 text-gray-500 text-xl" /> */}
            </div>
          </div>
        </div>
      </div>
      {isModalOpen && (
        <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <div className="p-4">
            {roomDescriptions[code] && product && (
              <DetailedRoomDescription
                description={roomDescriptions[code]}
                rawDescription={getFullRawDescription(product)}
                doAllowShowingRawRateDescription
              />
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

HotelRate.propTypes = {
  product: PropTypes.shape().isRequired,
  tripId: PropTypes.string.isRequired,
  hotelDetails: PropTypes.shape().isRequired,
};

export default HotelRate;
